import React from 'react';
import HeroSection from '../components/HeroSection';
import SEO from '../components/SEO';

const HERO_IMG = '/images/charities-hero.jpg';

// Descriptions pulled directly from each charity's own website or governance
// documents (Charity Commission register). Kept to 2–3 sentences each so the
// page scans quickly; follow the "Visit website" link for the full picture.
// Ordered local-first → national.
const CHARITIES = [
  {
    name: 'Glossopdale Foodbank',
    tag: 'Local · Reg. 1177306',
    desc: 'Independent local charity providing emergency food parcels and essentials to anyone in financial crisis across Glossop and the surrounding villages. Powered by around 70 volunteers running four distribution points — Glossop, Hadfield, Whitfield and Gamesley — and in 2025 delivered roughly 40,000 meals.',
    url: 'https://glossopdalefoodbank.org.uk/',
  },
  {
    name: 'The Hummingbird Project',
    tag: 'Local · Reg. 1209863',
    desc: "Volunteer-led Glossop charity running a clothing, baby and hygiene bank under a unique \u201CFreetail\u201D model — give what you can, take what you need. They distribute clothing, toiletries, period products, school uniforms and baby equipment to anyone who needs them, no questions asked.",
    url: 'https://hummingbirdhubs.co.uk/',
  },
  {
    name: 'Whitfield Community Centre',
    tag: 'Local · Reg. 1218126',
    desc: 'Volunteer-led community hub in the heart of Whitfield, home to the Whitfield Food Club, the Bread & Roses community café, youth groups, fitness classes and family events. Saved and rebuilt by the local community after the previous youth centre was closed in 2024 — a true place to belong.',
    url: 'https://whitfieldcommunitycentre.org/',
  },
  {
    name: 'Hadfield Coming Together',
    tag: 'Local · Reg. 1210435',
    desc: 'Hadfield-based community group based out of Carmel Church on Station Road. Runs weekly food parcels (Bellies not Bins), community meals for over-60s, cooking classes, warm spaces, free arts and crafts events, and litter picks.',
    url: 'https://www.facebook.com/groups/325716365918605/',
  },
  {
    name: 'G52 — Gamesley, be part of it',
    tag: 'Local · Reg. 1192129',
    desc: 'Volunteer-run community organisation serving the Gamesley estate "52 weeks of the year" since 2013. Runs a discounted food pantry, debt counselling sessions, a fuel bank, holiday clubs, teen residentials, cooking-on-a-budget classes, and even gives elderly residents lifts to dentist and GP appointments.',
    url: 'https://www.facebook.com/groups/G52Gamesley/',
  },
  {
    name: 'Bradbury Community House',
    tag: 'Local · Reg. 255051',
    desc: 'Independent charitable trust on Market Street providing meeting rooms and recreational space seven days a week, 9am–10:30pm, for community groups, social organisations, business groups and local people. Fully self-funded through affordable room hire — no outside grants.',
    url: 'https://bradbury-house.co.uk/',
  },
  {
    name: 'Age Concern Glossop & District',
    tag: 'Local · Reg. 220566',
    desc: 'Local charity based at Bradbury Community House working to improve later life for older people in Glossop and the surrounding district — through information, advice, companionship and community initiatives, including the much-loved annual festive tin appeal that delivers food parcels to elderly residents each Christmas.',
  },
  {
    name: 'Mentell',
    tag: 'Founded in Glossop · Reg. 1187547',
    desc: 'Founded here in Glossop and now a national charity, Mentell provides free, confidential peer-support "circles" where men aged 18+ can talk about whatever is on their mind — no advice, no judgement, no commitment. Online every Monday at 7pm, plus local in-person circles across England. We\u2019ve backed Mentell since the start and plan to keep doing so.',
    url: 'https://www.mentell.org.uk/',
  },
  {
    name: "Mummy\u2019s Star",
    tag: 'Founded in Glossop · Reg. 1152808',
    desc: 'Founded here in Glossop and now the only UK and Ireland charity supporting women and birthing people diagnosed with cancer during pregnancy or within a year of giving birth — along with their partners and families. Offers emotional support, peer forums, hospital-appointment advocacy, employment-rights advice and small financial grants. We\u2019ve supported them from day one.',
    url: 'https://www.mummysstar.org/',
  },
];

function CharityCard({ charity }) {
  return (
    <article
      data-testid={`charity-card-${charity.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
      className="bg-white border border-zinc-200 p-6 sm:p-7 flex flex-col hover:border-[#F0A500] transition-colors"
    >
      <div className="flex items-start justify-between gap-4 mb-3">
        <h2 className="text-xl font-bold tracking-tight text-[#0A0A0A]">{charity.name}</h2>
        <span className="text-[10px] uppercase tracking-widest font-bold text-[#F0A500] whitespace-nowrap mt-1">{charity.tag}</span>
      </div>
      <p className="text-sm text-[#52525B] leading-relaxed flex-1">{charity.desc}</p>
      {charity.url && (
        <a
          href={charity.url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-5 text-sm font-bold uppercase tracking-wider text-[#F0A500] hover:text-[#D99500] self-start"
        >
          {charity.urlLabel || 'Visit website'} &rarr;
        </a>
      )}
    </article>
  );
}

export default function CharitiesPage() {
  return (
    <div data-testid="charities-page">
      <SEO
        title="Charities We Support"
        description="A sample of the local Glossop charities and national good causes supported through Glossop & District Round Table 916 fundraising."
        path="/charities"
        keywords={['Glossop charities', 'local good causes Glossop', 'Round Table 916 beneficiaries', 'Glossopdale Foodbank', 'Hummingbird Project Glossop', 'Mentell', "Mummy's Star"]}
      />

      <HeroSection
        image={HERO_IMG}
        title="Charities We Support"
        subtitle="A sample of the local Glossop and national good causes supported by Round Table 916 fundraising."
        fullHeight={false}
        alt="Glossop & District Round Table 916 presenting a £20,200 cheque to Glossop & District Charities, surrounded by Round Table members, the Mayor and representatives of the supported charities"
      />

      <section className="py-16 sm:py-20 bg-white px-4">
        <div className="max-w-3xl mx-auto">
          <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#F0A500] block mb-3">
            Where the money goes
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0A0A0A] mb-6">
            Local good causes, powered by Glossop
          </h2>
          <div className="space-y-4 text-base text-[#52525B] leading-relaxed">
            <p>
              Every ticket, every pint and every penny raised at a Round Table event ends up on this page. We&rsquo;re a small group of volunteers &mdash; the real work is done by the charities and community groups below, and we&rsquo;re proud to help where we can.
            </p>
            <p>
              We&rsquo;ve supported all of these local charities for many years &mdash; and hope to do so for many more years to come.
            </p>
            <p>
              This is a sample list, not an exhaustive one &mdash; we support new causes every year through our open <strong>donation request</strong> scheme. If you run a local good cause, applications are open to be considered for a grant.
            </p>
          </div>
        </div>
      </section>

      <section className="pb-20 sm:pb-28 bg-white px-4">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {CHARITIES.map((c) => (
              <CharityCard key={c.name} charity={c} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
